import { z } from 'zod';
import { id as CanonicalId } from '../../artifacts/models/common.mjs';
import { SlotPresentation } from './slot-presentation.mjs';
import { ImageId as Id } from '../../artifacts/images/values.mjs';

export { CanonicalId, Id };
export const SymbolicPath = z
  .array(
    z
      .string()
      .regex(/^[A-Za-z_][A-Za-z0-9_-]*$/u)
      .refine((name) => !['__proto__', 'prototype', 'constructor'].includes(name)),
  )
  .min(1)
  .max(8);

export const Digest = z.string().regex(/^sha256:[a-f0-9]{64}$/u);
export const Instant = z.iso.datetime({ offset: true });
export const Rect = z.strictObject({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().nonnegative(),
  height: z.number().finite().nonnegative(),
});
export const Scope = z.enum(['source-site', 'instance']);
export const Property = z.enum([
  'text',
  'colour',
  'spacing',
  'numberFormat',
  'imageFit',
  'imageRatio',
]);
export const Source = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('code'),
    file: z.string().min(1).max(512),
    start: z.int().nonnegative(),
    end: z.int().nonnegative(),
    line: z.int().positive(),
    column: z.int().nonnegative(),
  }),
  z.strictObject({
    kind: z.literal('artifact-node'),
    nodeId: CanonicalId,
    propertyPath: SymbolicPath,
    parentNodeId: CanonicalId.nullable().optional(),
  }),
  z.strictObject({ kind: z.literal('visual-region'), regionId: Id }),
]);
export const Slot = z.strictObject({
  slotId: Id,
  property: Property,
  scope: Scope,
  kind: z.enum([
    'jsx-literal',
    'presentation-token',
    'gap-token',
    'declared-formatter',
    'artifact-property',
  ]),
  presentation: SlotPresentation.optional(),
});
export const Entry = z.strictObject({
  pid: Id,
  targetId: Id,
  source: Source,
  component: z.strictObject({
    name: Id,
    mapping: z.enum(['exact', 'authored-owner', 'visual-region']),
  }),
  fingerprint: Digest,
  editSlots: z.array(Slot).max(32),
  bindingRefs: z.array(CanonicalId).max(32),
});
export const BuildManifest = z.strictObject({
  schemaVersion: z.literal(1),
  taggerVersion: Id,
  buildId: Id,
  revisionId: CanonicalId,
  artifactId: CanonicalId.optional(),
  nativeRootNodeId: CanonicalId.optional(),
  sourceDigest: Digest,
  assetsDigest: Digest,
  entries: z.array(Entry).max(50000),
  coverage: z.strictObject({
    exact: z.int().nonnegative(),
    owner: z.int().nonnegative(),
    visualRegion: z.int().nonnegative(),
    complete: z.boolean(),
  }),
});
export const Target = z.strictObject({
  selectionId: Id,
  targetId: Id,
  pid: Id,
  sourceSiteRef: Id,
  renderedInstanceRef: Id.nullable(),
  bindingRefs: z.array(CanonicalId).max(32),
  scope: Scope,
});
export const SelectionManifest = z.strictObject({
  schemaVersion: z.literal(1),
  selectionSetId: Id,
  companyId: CanonicalId,
  artifactId: CanonicalId,
  revisionId: CanonicalId,
  buildId: Id,
  manifestDigest: Digest,
  createdBy: CanonicalId,
  targets: z.array(Target).min(1).max(16),
  cropRefs: z.array(Id).max(16),
});
export const Candidate = z.strictObject({
  pid: Id,
  kind: z.enum([
    'text',
    'element',
    'image',
    'chart-mark',
    'slide-object',
    'column',
    'region',
    'component',
    'visual-region',
  ]),
  rects: z.array(Rect).max(8),
  instanceRef: Id.nullable(),
});
export const GeoIdentity = z.strictObject({
  sessionId: Id,
  revision: CanonicalId,
  docGeneration: z.int().nonnegative(),
  requestId: Id,
  space: z.literal('host-css'),
  epoch: z.int().nonnegative(),
});
export const TargetHint = z.strictObject({ pid: Id, instanceRef: Id.nullable(), scope: Scope });
export const Destination = z.strictObject({
  kind: z.enum(['thread', 'mcp-session', 'terminal-session']),
  id: Id,
});
export const ResolveInput = z.strictObject({
  artifactId: CanonicalId,
  revisionId: CanonicalId,
  buildId: Id,
  targets: z.array(TargetHint).min(1).max(16),
});
export const FreezeInput = ResolveInput.extend({
  destination: Destination,
  cropRefs: z.array(Id).max(16),
});
export const SelectionRead = z.strictObject({ selectionSetId: Id, destination: Destination });

// Refuse objects with accessors/custom prototypes before a schema can read fields.
export function plainData(value, depth = 0, maximumDepth = 24) {
  if (depth > maximumDepth) throw new TypeError('input_too_deep');
  if (value === null || typeof value !== 'object') return;
  if (!plainContainer(value)) throw new TypeError('input_not_plain');
  for (const [key, field] of Object.entries(Object.getOwnPropertyDescriptors(value))) {
    if (forbiddenField(key, field)) throw new TypeError('input_not_plain');
    plainData(field.value, depth + 1, maximumDepth);
  }
}
function plainContainer(value) {
  const prototype = Object.getPrototypeOf(value);
  return Array.isArray(value) || prototype === Object.prototype || prototype === null;
}
function forbiddenField(key, field) {
  return ['__proto__', 'prototype', 'constructor'].includes(key) || !!field.get || !!field.set;
}
export function parse(schema, input) {
  plainData(input);
  return schema.parse(input);
}
